
import SlotMap from '@neumatter/slot-map'
import {
  getCanonicalLocales,
  getOption,
  resolveLocale,
  SupportedLocales
} from './utils.js'
import segmentationRules from './data/segmentationRules.js'

const replaceVariables = (variables, input) => {
  const findVarRegex = /\$[A-Za-z0-9_]+/gm
  return input.replaceAll(findVarRegex, match => {
    if (!(match in variables)) {
      throw new Error(`No such variable ${match}`)
    }
    return variables[match]
  })
}

const isSurrogate = (str, pos) => {
  return (
    0xd800 <= str.charCodeAt(pos - 1) &&
    str.charCodeAt(pos - 1) <= 0xdbff &&
    0xdc00 <= str.charCodeAt(pos) &&
    str.charCodeAt(pos) <= 0xdfff
  )
}

/**
 * Adds $ to before rules and ^ to after rules for strickness
 * Replaces variables
 * Initializes the RegExp
 *
 * @param rule raw rule string from cldr-segmentation-rules.generated
 * @param variables
 * @param after appends ^ if true and $ if false
 * @returns
 */
const generateRuleRegex = (rule, variables, after) => {
  return new RegExp(
    `${after ? '^' : ''}${replaceVariables(variables, rule)}${after ? '' : '$'}`
  )
}

const prepareLocaleSegmentationRules = segmentationTypeValue => {
  const preparedRules = {}
  for (const ruleNr of Object.keys(segmentationTypeValue.segmentRules)) {
    const ruleValue = segmentationTypeValue.segmentRules[ruleNr]
    const preparedRule = {
      breaks: ruleValue.breaks
    }

    if ('before' in ruleValue && ruleValue.before) {
      preparedRule.before = generateRuleRegex(
        ruleValue.before,
        segmentationTypeValue.variables,
        false
      )
    }
    if ('after' in ruleValue && ruleValue.after) {
      preparedRule.after = generateRuleRegex(
        ruleValue.after,
        segmentationTypeValue.variables,
        true
      )
    }

    preparedRules[ruleNr] = preparedRule
  }
  return preparedRules
}

const breaksAtResult = (breaks, matchingRule) => ({
  breaks,
  matchingRule
})

export default class Segmenter {
  #mergedSegmentationTypeValue
  #rules
  #ruleSortedKeys

  constructor (locales, options) {
    const requestedLocales = getCanonicalLocales(locales)
    const availableLocales = SlotMap.getInternal(Segmenter, 'availableLocales')
    options = options || {}
    const opts = Object.create(null)

    const matcher = getOption(
      options,
      'localeMatcher',
      'string',
      ['lookup', 'best fit'],
      'best fit'
    )

    opts.localeMatcher = matcher

    const granularity = getOption(
      options,
      'granularity',
      'string',
      ['word', 'sentence', 'grapheme'],
      'grapheme'
    )

    const r = resolveLocale(
      availableLocales, // availible locales
      requestedLocales,
      opts,
      [], // there is no relevantExtensionKeys
      {},
      () => '' // use only root rules
    )

    SlotMap.setInternal(this, 'locale', r.locale)
    // root rules based on granularity
    this.#mergedSegmentationTypeValue = segmentationRules.root[granularity]
    // merge root rules with locale ones if locale is specified
    if (r.locale.length) {
      const localeOverrides = segmentationRules[r.locale]
      if (granularity in localeOverrides) {
        const localeSegmentationTypeValue = localeOverrides[granularity]
        this.#mergedSegmentationTypeValue.variables = {
          ...this.#mergedSegmentationTypeValue.variables,
          ...localeSegmentationTypeValue.variables
        }
        this.#mergedSegmentationTypeValue.segmentRules = {
          ...this.#mergedSegmentationTypeValue.segmentRules,
          ...localeSegmentationTypeValue.segmentRules
        }
        this.#mergedSegmentationTypeValue.suppressions = [
          ...this.#mergedSegmentationTypeValue.suppressions,
          ...localeSegmentationTypeValue.suppressions
        ]
      }
    }

    // prepare rules
    this.#rules = prepareLocaleSegmentationRules(
      this.#mergedSegmentationTypeValue
    )

    // order rule keys
    this.#ruleSortedKeys = Object.keys(this.#rules).sort(
      (a, b) => Number(a) - Number(b)
    )
  }

  breaksAt (position, input) {
    const ruleSortedKeys = this.#ruleSortedKeys
    const rules = this.#rules
    const mergedSegmentationTypeValue = this.#mergedSegmentationTypeValue

    // artificial rule 0.2
    if (position === 0) {
      return breaksAtResult(true, '0.2')
    }

    if (position === input.length) {
      // rule 0.3
      return breaksAtResult(true, '0.3')
    }

    // artificial rule 0.1: js specific, due to es5 regex not being unicode aware
    // number 0.1 chosen to mimic java implementation, but needs to execute after 0.2 and 0.3 to be inside the string bounds
    if (isSurrogate(input, position)) {
      return breaksAtResult(false, '0.1')
    }

    const stringBeforeBreak = input.substring(0, position)
    const stringAfterBreak = input.substring(position)

    // artificial rule 0.4: handle suppressions
    if ('suppressions' in mergedSegmentationTypeValue) {
      for (const suppressions of mergedSegmentationTypeValue.suppressions) {
        if (stringBeforeBreak.trim().endsWith(suppressions)) {
          return breaksAtResult(false, '0.4')
        }
      }
    }

    // loop through rules and find a match
    for (const ruleKey of ruleSortedKeys) {
      const { before, after, breaks } = rules[ruleKey]
      // for debugging
      // if (ruleKey === '16' && position === 4) {
      //   console.log({before, after, stringBeforeBreak, stringAfterBreak})
      // }
      if (before) {
        if (!before.test(stringBeforeBreak)) {
          // didn't match the before part, therfore skipping
          continue
        }
      }

      if (after) {
        if (!after.test(stringAfterBreak)) {
          // didn't match the after part, therfore skipping
          continue
        }
      }

      return breaksAtResult(breaks, ruleKey)
    }

    // artificial rule 999: if no rule matched is Any ÷ Any so return true
    return breaksAtResult(true, '999')
  }

  segment (input) {
    return new SegmentIterator(this, input)
  }

  resolvedOptions () {
    return {
      locale: SlotMap.getInternal(this, 'locale'),
      granularity: SlotMap.getInternal(this, 'granularity')
    }
  }

  static supportedLocalesOf (locales, options) {
    const availableLocales = SlotMap.getInternal(Segmenter, 'availableLocales')

    return SupportedLocales(
      availableLocales,
      getCanonicalLocales(locales),
      options
    )
  }

  static get polyfilled () {
    return true
  }
}

SlotMap.setInternal(
  Segmenter,
  'availableLocales',
  new Set(Object.keys(segmentationRules).filter(key => key !== 'root'))
)

const createSegmentDataObject = (
  segmenter,
  segment,
  index,
  input,
  matchingRule
) => {
  const returnValue = {
    segment,
    index,
    input
  }
  if (SlotMap.getInternal(segmenter, 'granularity') === 'word') {
    returnValue.isWordLike = matchingRule !== '3.1' && matchingRule !== '3.2'
  }
  return returnValue
}

class SegmentIterator {
  #segmenter
  #lastSegmentIndex
  #input
  constructor (segmenter, input) {
    this.#segmenter = segmenter
    this.#lastSegmentIndex = 0
    if (typeof input === 'symbol') {
      throw TypeError('Input must not be a symbol')
    }
    this.#input = String(input)
  }

  [Symbol.iterator] () {
    return new SegmentIterator(this.#segmenter, this.#input)
  }

  next () {
    // using only the relevant bit of the string
    let checkString = this.#input.substring(this.#lastSegmentIndex)

    // loop from the start of the checkString, until exactly length (breaksAt returns break at pos=== lenght)
    for (let position = 1; position <= checkString.length; position++) {
      const { breaks, matchingRule } = this.#segmenter.breaksAt(
        position,
        checkString
      )
      if (breaks) {
        const segment = checkString.substring(0, position)
        const index = this.#lastSegmentIndex
        this.#lastSegmentIndex += position

        return {
          done: false,
          value: createSegmentDataObject(
            this.#segmenter,
            segment,
            index,
            this.#input,
            matchingRule
          )
        }
      }
    }
    // no segment was found by the loop, therefore the segmentation is done
    return { done: true, value: undefined }
  }

  containing (positionInput) {
    if (typeof positionInput === 'bigint') {
      throw TypeError('Index must not be a BigInt')
    }

    let position = Number(positionInput)

    // https://tc39.es/ecma262/#sec-tointegerorinfinity
    // 2. If number is NaN, +0𝔽, or -0𝔽, return 0.
    if (isNaN(position) || !position) {
      position = 0
    }
    // 5. Let integer be floor(abs(ℝ(number))).
    // 6. If number < -0𝔽, set integer to -integer.
    position = Math.floor(Math.abs(position)) * (position < 0 ? -1 : 1)

    if (position < 0 || position >= this.#input.length) {
      return undefined
    }

    // find previous break point
    let previousBreakPoint = 0
    if (position === 0) {
      previousBreakPoint = 0
    } else {
      const checkString = this.#input
      for (let cursor = position; cursor >= 0; cursor--) {
        const { breaks } = this.#segmenter.breaksAt(cursor, checkString)
        if (breaks) {
          previousBreakPoint = cursor
          break
        }
      }
    }
    let checkString = this.#input.substring(previousBreakPoint)
    // find next break point
    for (let cursor = 1; cursor <= checkString.length; cursor++) {
      const { breaks, matchingRule } = this.#segmenter.breaksAt(
        cursor,
        checkString
      )
      if (breaks) {
        const segment = checkString.substring(0, cursor)
        return createSegmentDataObject(
          this.#segmenter,
          segment,
          previousBreakPoint,
          this.#input,
          matchingRule
        )
      }
    }
  }
}
